import { useNavigate } from 'react-router-dom'

import { OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import { Add, Delete } from '@factorialco/f0-react/icons/app'
import { AllFormsDataCollectionDocument } from 'generated/resources/graphql'

import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useActivities } from 'modules/core/lib/activities/provider'
import i18n from 'modules/core/lib/i18n'

import { getFormStatusFilter } from 'modules/engagement_surveys/lib/filters'
import { DEFAULT_DATA_COLLECTION_STORAGE } from 'modules/trainings/lib/collections'
import { translateFormType } from 'modules/trainings/lib/trainingFormTypes'

export const AllFormsDataCollection = ({ trainingId }: { trainingId: string }) => {
  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const { openActivity } = useActivities()

  const source = useGraphqlDataCollectionSource({
    document: AllFormsDataCollectionDocument,
    paginationType: 'pages',
    perPage: 30,
    mapData: (data) => data.forms.formsConnection.edges.map((e) => e.node),
    mapPagination: (data) => ({ total: data.forms.formsConnection.totalCount ?? 0 }),
    mapVariables: ({ filters, search }) => ({
      resourceId: trainingId.toString(),
      search,
      status: filters.status,
    }),
    itemOnClick: (item) => () => {
      navigate(
        navigation.trainings.list.details_revamp.trainingSurveyDetail.questions.fullPath({
          id: trainingId,
          formId: String(item.id),
        })
      )
    },
    itemActions: (item) =>
      [
        ...(item.status === 'draft'
          ? [
              {
                label: i18n.t('trainings.surveys.all_forms_datatable.delete'),
                icon: Delete,
                onClick: () => {
                  openActivity('trainings.deleteSurvey', {
                    trainingId: trainingId.toString(),
                    formId: item.id.toString(),
                  })
                },
              },
            ]
          : []),
      ].filter(Boolean),
    filters: {
      status: getFormStatusFilter(['draft', 'active']),
    },
    search: { enabled: true },
    primaryActions: () => ({
      label: i18n.t('trainings.surveys.all_forms_datatable.new_survey_button'),
      icon: Add,
      onClick: async () => {
        navigate(
          navigation.trainings.list.details_revamp.view.surveys.select.fullPath({
            id: trainingId,
          })
        )
      },
    }),
  })

  return (
    <OneDataCollection
      id="trainings/all_forms/v1"
      storage={DEFAULT_DATA_COLLECTION_STORAGE}
      source={source}
      visualizations={[
        {
          type: 'table',
          options: {
            columns: [
              {
                label: i18n.t('trainings.surveys.all_forms_datatable.column_name'),
                render: (item) => ({
                  type: 'text',
                  value: item.title,
                }),
              },
              {
                label: i18n.t('trainings.surveys.status.column_title'),
                render: (item) => ({
                  type: 'status',
                  value: {
                    label:
                      item.status === 'draft'
                        ? i18n.t('trainings.surveys.status.draft')
                        : i18n.t('trainings.surveys.status.published'),
                    status: item.status === 'draft' ? 'neutral' : 'positive',
                  },
                }),
              },
              {
                label: i18n.t('trainings.surveys.type.column_title'),
                render: (item) => ({
                  type: 'text',
                  value: translateFormType(item.formType),
                }),
              },
              {
                label: i18n.t('trainings.surveys.all_forms_datatable.participation_rate'),
                render: (item) => {
                  const answered = Number(item.answeredCount ?? 0)
                  const sent = Number(item.sentCount ?? 0)
                  const percentage = sent > 0 ? Number(((answered / sent) * 100).toFixed(1)) : 0
                  return {
                    type: 'percentage',

                    value: {
                      label: `${percentage}%`,
                      percentage: percentage,
                    },
                  }
                },
              },
            ],
            frozenColumns: 1,
          },
        },
      ]}
    />
  )
}
